import React, { useState, useRef, useEffect, useMemo } from "react";
import { FaPlayCircle, FaTimes } from "react-icons/fa";
import searchImg from "../../../../assets/search.png";
import arrowdown from "../../../../assets/arrow-down.png";
import GlowButton from "../../GlowButton";

const GRADIENTS = [
  "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
  "linear-gradient(135deg, #0f3460 0%, #533483 100%)",
  "linear-gradient(135deg, #2d1b69 0%, #11998e 100%)",
  "linear-gradient(135deg, #360033 0%, #0b8793 100%)",
  "linear-gradient(135deg, #1f4037 0%, #99f2c8 100%)",
  "linear-gradient(135deg, #373b44 0%, #4286f4 100%)",
  "linear-gradient(135deg, #4b1248 0%, #f10711 100%)",
  "linear-gradient(135deg, #200122 0%, #6f0000 100%)",
];

function getGradient(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

function VideoThumbnail({ vidUrl, title, onPlay }) {
  const [frameLoaded, setFrameLoaded] = useState(false);
  const gradient = getGradient(title);

  return (
    <div
      className="relative w-full h-full overflow-hidden cursor-pointer"
      style={{ background: gradient }}
      onClick={onPlay}
    >
      <video
        src={`${vidUrl}#t=2`}
        preload="metadata"
        muted
        playsInline
        onLoadedData={() => setFrameLoaded(true)}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
        style={{ opacity: frameLoaded ? 1 : 0 }}
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/35">
        <FaPlayCircle className="text-white text-[72px] opacity-90" />
      </div>
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.beemxyz.com";

const DemoNavContent = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [search, setSearch] = useState("");
  const [showMore, setShowMore] = useState(false);

  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/common/tutorial-videos?type=demo&platform=marketing`)
      .then((res) => res.json())
      .then((json) => {
        const fetched = (json?.data?.videos ?? []).map((v) => ({
          title: v.headerName,
          desc: "get a closer look at how beɘmz transforms operations and customer experience.",
          category: "demo",
          vidUrl: v.videoUrl,
        }));
        setVideos(fetched);
      })
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  const INITIAL_COUNT = 2;
  const isSearching = search.trim() !== "";

  // When searching, filter all videos; otherwise respect show more/less
  const filtered = useMemo(() => {
    if (isSearching) {
      return videos.filter((v) =>
        v.title.toLowerCase().includes(search.trim().toLowerCase())
      );
    }
    return showMore ? videos : videos.slice(0, INITIAL_COUNT);
  }, [search, videos, showMore, isSearching]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) setPlayingIndex(null);
        });
      },
      { threshold: 0.2 }
    );
    if (section1Ref.current) observer.observe(section1Ref.current);
    if (section2Ref.current) observer.observe(section2Ref.current);
    return () => observer.disconnect();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-40 text-gray-400 text-lg">
        loading videos...
      </div>
    );
  }

  return (
    <>
      {/* Search Section */}
      <div className="flex w-full sm:max-w-[290px] md:max-w-[500px] xl:max-w-[600px] mt-10 mx-auto gap-2 pb-[30px] sm:mb-[-4rem]">
        <div className="relative flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") setSearch(""); }}
            placeholder="search videos..."
            className="w-full border border-[#D0D5DD] rounded-full py-2 px-8 sm:py-2.5 sm:px-10 md:py-3 md:px-12 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
            aria-label="Search demos"
            ref={searchInputRef}
          />
          <span className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
            <img src={searchImg} alt="search icon" className="w-4 h-4 sm:w-5 sm:h-5 opacity-70" />
          </span>
        </div>
        <button
          type="button"
          onClick={() => search ? setSearch("") : null}
          className="bg-[#6E3FC6] text-white px-4 py-2 sm:px-5 sm:py-2 rounded-full font-bold hover:bg-[#5b32a3] transition"
        >
          {search ? "clear" : "search"}
        </button>
      </div>

      {/* Video Rows */}
      {filtered.map((item, idx) => (
        <div key={idx}>
          {idx % 2 === 0 ? (
            <div
              ref={section1Ref}
              id="section1"
              className="flex flex-col md:flex-row items-center justify-center w-full h-screen md:h-[400px] xl-2xl:h-[500px] bg-white overflow-hidden"
            >
              {/* Left - Text */}
              <div className="flex flex-col justify-center items-start w-full md:w-1/2 h-full px-8 md:px-16 space-y-5">

                <h1 className="text-3xl md:text-[34px] xl-2xl:text-[44px] font-semibold font-Lexend text-gray-900 md:leading-[38px] lowercase">
                  {item.title}
                </h1>
                <p className="text-[#4D4D4D] sm:text-[19px] md:text-[19px] lg-xl:text-[22px] max-w-[295px] xl-2xl:max-w-[400px] leading-[24px]">
                  {item.desc}
                </p>
                <span className="mt-0 flex justify-center sm:mr-[30px] ml-0 items-start">
                  <GlowButton btnValue="free trial" btn1="2" onClick={() => window.location.href = "/business/get-started"} />
                </span>
              </div>

              {/* Right - Video */}
              <div className="relative w-full md:w-1/2 h-full overflow-hidden bg-black">
                {playingIndex !== idx ? (
                  <VideoThumbnail vidUrl={item.vidUrl} title={item.title} onPlay={() => setPlayingIndex(idx)} />
                ) : (
                  <div className="absolute inset-0 bg-black">
                    <button onClick={() => setPlayingIndex(null)} className="absolute top-3 right-3 z-10 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition">
                      <FaTimes />
                    </button>
                    <video src={item.vidUrl} autoPlay controls className="w-full h-full object-contain" />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div
              ref={section2Ref}
              id="section2"
              className="flex flex-col md:flex-row items-center justify-center w-full h-screen md:h-[400px] xl-2xl:h-[500px] bg-white overflow-hidden"
            >
              {/* Left - Video */}
              <div className="relative w-full md:w-1/2 h-full overflow-hidden bg-black">
                {playingIndex !== idx ? (
                  <VideoThumbnail vidUrl={item.vidUrl} title={item.title} onPlay={() => setPlayingIndex(idx)} />
                ) : (
                  <div className="absolute inset-0 bg-black">
                    <button onClick={() => setPlayingIndex(null)} className="absolute top-3 right-3 z-10 text-white bg-black/50 rounded-full p-2 hover:bg-black/70 transition">
                      <FaTimes />
                    </button>
                    <video src={item.vidUrl} autoPlay controls className="w-full h-full object-contain" />
                  </div>
                )}
              </div>

              {/* Right - Text */}
              <div className="flex flex-col justify-center items-start w-full md:w-1/2 h-full px-8 md:px-16 space-y-6">

                <h1 className="text-3xl md:text-[34px] xl-2xl:text-[44px] font-semibold font-Lexend text-gray-900 md:leading-[38px] lowercase">
                  {item.title}
                </h1>
                <p className="text-[#4D4D4D] sm:text-[19px] md:text-[19px] lg-xl:text-[22px] max-w-[295px] xl-2xl:max-w-[400px] leading-[24px]">
                  {item.desc}
                </p>
                <span className="mt-0 flex justify-center sm:mr-[30px] ml-0 items-start">
                  <GlowButton btnValue="free trial" btn1="2" onClick={() => window.location.href = "/business/get-started"} />
                </span>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* View More / View Less — hidden while searching */}
      {!isSearching && videos.length > INITIAL_COUNT && (
        <div className="flex justify-center items-center mt-10 sm:mt-8 pb-[80px]">
          {!showMore ? (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="flex px-4 py-2 sm:px-6 rounded-[10px] bg-purple-50 border border-gray-300 hover:border-gray-400 hover:bg-purple-100 transition text-[#6941C6] font-semibold"
            >
              <img src={arrowdown} alt="View more" />
              <span className="font-inter font-medium text-[18px] leading-[24px] tracking-normal">view more</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setShowMore(false)}
              className="flex px-4 py-2 sm:px-6 rounded-[10px] bg-purple-50 border border-gray-300 hover:border-gray-400 hover:bg-purple-100 transition text-[#6941C6] font-semibold"
            >
              <img src={arrowdown} alt="View less" />
              <span className="font-inter font-medium text-[18px] leading-[24px] tracking-normal">view less</span>
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 && !loading && (
        <div className="pb-12 lowercase font-semibold text-center py-20 text-gray-400">
          no match found
        </div>
      )}
    </>
  );
};

export default DemoNavContent;
